const TERMINAL_ID_STORAGE_KEY = 'terminalId';

const getElectronApi = () => window.electronAPI;

// Claves para las que la última escritura a setSecureToken falló (ver setToken) y el valor
// vigente quedó únicamente en localStorage. Mientras una clave esté marcada, getToken no debe
// confiar en lo que haya en el storage cifrado -- puede ser un valor viejo que nunca se
// terminó de reemplazar. Vive en memoria (por proceso), no hace falta persistirla: si la app
// se reinicia y el problema de disco sigue, un 401 real fuerza un login nuevo igual.
const staleSecureTokenKeys = new Set();

const generateBrowserTerminalId = () => {
  if (window.crypto?.randomUUID) {
    return `WEB-${window.crypto.randomUUID()}`;
  }

  return `WEB-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const getOrCreateBrowserTerminalId = () => {
  const existingTerminalId = localStorage.getItem(TERMINAL_ID_STORAGE_KEY);
  if (existingTerminalId) {
    return existingTerminalId;
  }

  const terminalId = generateBrowserTerminalId();
  localStorage.setItem(TERMINAL_ID_STORAGE_KEY, terminalId);
  return terminalId;
};

const base64ToPdfUrl = (base64) => {
  if (!base64) {
    return null;
  }

  const cleanBase64 = base64.replace(/^data:application\/pdf;base64,/, '');
  const byteCharacters = atob(cleanBase64);
  const byteNumbers = new Array(byteCharacters.length);

  for (let index = 0; index < byteCharacters.length; index += 1) {
    byteNumbers[index] = byteCharacters.charCodeAt(index);
  }

  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: 'application/pdf' });
  return window.URL.createObjectURL(blob);
};

const openPdf = (base64) => {
  const pdfUrl = base64ToPdfUrl(base64);
  if (!pdfUrl) {
    return;
  }

  window.open(pdfUrl, '_blank', 'noopener,noreferrer');

  setTimeout(() => {
    try {
      window.URL.revokeObjectURL(pdfUrl);
    } catch (_error) {
      // no-op
    }
  }, 60_000);
};

const PlatformBridge = {
  isDesktop() {
    return Boolean(getElectronApi());
  },

  async getTerminalId() {
    const electronApi = getElectronApi();

    if (electronApi?.getDeviceId) {
      const id = await electronApi.getDeviceId();
      if (id) {
        localStorage.setItem(TERMINAL_ID_STORAGE_KEY, id);
        return id;
      }
    }

    return getOrCreateBrowserTerminalId();
  },

  async playNotificationSound() {
    const electronApi = getElectronApi();

    if (electronApi?.playNotificationSound) {
      return electronApi.playNotificationSound();
    }

    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    if (audioContext.state === 'suspended') {
      await audioContext.resume().catch(() => {});
    }

    const now = audioContext.currentTime;
    const playTone = (frequency, startTime, duration, volume = 0.35) => {
      const oscillator = audioContext.createOscillator();
      const gain = audioContext.createGain();

      oscillator.connect(gain);
      gain.connect(audioContext.destination);
      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';

      gain.gain.setValueAtTime(0, startTime);
      gain.gain.linearRampToValueAtTime(volume, startTime + 0.02);
      gain.gain.setValueAtTime(volume, startTime + duration - 0.05);
      gain.gain.linearRampToValueAtTime(0, startTime + duration);

      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    };

    playTone(880, now, 0.2, 0.35);
    playTone(1100, now + 0.28, 0.25, 0.35);
    return true;
  },

  async printPdf(base64) {
    const electronApi = getElectronApi();

    if (electronApi?.printPDF) {
      await electronApi.printPDF(base64);
      return true;
    }

    openPdf(base64);
    return false;
  },

  openPdf,

  onAppUpdateStatus(callback) {
    const electronApi = getElectronApi();
    if (!electronApi?.onAppUpdateStatus) {
      return () => {};
    }

    return electronApi.onAppUpdateStatus(callback);
  },

  async installAppUpdate() {
    const electronApi = getElectronApi();
    if (!electronApi?.installAppUpdate) {
      return false;
    }

    await electronApi.installAppUpdate();
    return true;
  },

  // Dispara el chequeo de actualización del dashboard de escritorio. Distinto
  // de setToken("accessToken", ...): ese también se llama en cada refresh de
  // token y ya no debe chequear actualizaciones — solo login real y apertura
  // de la app (esto último ya lo hace main.js solo, sin IPC).
  notifyLogin() {
    const electronApi = getElectronApi();
    if (!electronApi?.notifyLogin) {
      return;
    }

    electronApi.notifyLogin();
  },

  // Storage seguro para tokens de sesión: en Electron usa safeStorage
  // (cifrado, respaldado por el keychain del OS) vía IPC. En modo web no
  // hay Electron disponible, así que cae a localStorage — mismo
  // comportamiento que tenía antes en ese modo.
  async getToken(key) {
    const electronApi = getElectronApi();
    if (electronApi?.getSecureToken) {
      // Si la última escritura de esta clave falló (ver setToken), el storage cifrado puede
      // tener un valor viejo que nunca se llegó a reemplazar -- el vigente quedó en
      // localStorage. No hay que preferir el cifrado en ese caso, aunque exista.
      if (!staleSecureTokenKeys.has(key)) {
        const secureValue = await electronApi.getSecureToken(key);
        if (secureValue) return secureValue;
      }
      // Storage cifrado vacío (o descartado por estar potencialmente desactualizado):
      // localStorage puede tener el valor vigente, sea por la migración única desde la
      // versión anterior de la app (que guardaba estos tokens en texto plano) o por el
      // fallback de arriba. Se reintenta migrarlo al storage cifrado.
      const legacyValue = localStorage.getItem(key);
      if (legacyValue) {
        // setSecureToken devuelve false sin persistir nada si safeStorage no está
        // disponible (ver secure-token-set en main.js, ej. Linux sin keyring). Si no
        // confirma que quedó guardado, no se borra el original: si igual se borrara,
        // la próxima lectura no encontraría el valor en ningún lado (ni cifrado, que
        // nunca se guardó, ni en localStorage, ya borrado) y la sesión se perdería en
        // la siguiente request.
        const migrated = await electronApi.setSecureToken(key, legacyValue);
        if (migrated) {
          localStorage.removeItem(key);
          staleSecureTokenKeys.delete(key);
        }
        return legacyValue;
      }
      return null;
    }
    return localStorage.getItem(key);
  },

  async setToken(key, value) {
    const electronApi = getElectronApi();
    if (electronApi?.setSecureToken) {
      // setSecureToken devuelve false sin persistir nada si safeStorage no está disponible o
      // si falla la escritura a disco (ver secure-token-set en main.js). Si no confirma que
      // quedó guardado, cae a localStorage: sin este fallback, un login nuevo en esas
      // condiciones no guardaría el token en ningún lado -- el estado de React se actualiza
      // igual, pero la siguiente request de Axios sale sin Authorization.
      const persisted = await electronApi.setSecureToken(key, value);
      if (persisted) {
        staleSecureTokenKeys.delete(key);
        return;
      }
      // El storage cifrado puede seguir teniendo un valor anterior (el de antes de este
      // intento fallido) -- se marca para que getToken no lo devuelva como si fuera el
      // vigente mientras el valor real está en localStorage.
      staleSecureTokenKeys.add(key);
    }
    localStorage.setItem(key, value);
  },

  async removeToken(key) {
    staleSecureTokenKeys.delete(key);
    const electronApi = getElectronApi();
    if (electronApi?.deleteSecureToken) {
      await electronApi.deleteSecureToken(key);
    }
    // Se borra en los dos lados sin importar si hay Electron: el fallback de setToken puede
    // haber dejado el token vigente en localStorage, y un logout no debe dejarlo atrás.
    localStorage.removeItem(key);
  }
};

export default PlatformBridge;
